import { Dispatch, SetStateAction, useEffect, useState } from 'react';
import { useIdleTimer } from 'react-idle-timer';
import { useSelector } from 'react-redux';
import { useAuth } from '../modules/profile/logic/auth';
import { selectInactivityMinutes, selectIsIdleTimeoutEnabled } from '../store/appSlice';

interface InactivitySessionReturn {
  /**
   * State to indicate whether inactivity window opened
   */
  open: boolean;
  /**
   * Set open control state for inactivity window
   */
  setOpen: Dispatch<SetStateAction<boolean>>;
}

export const useInactivitySession = (): InactivitySessionReturn => {
  const inactivityMinute = useSelector(selectInactivityMinutes);
  const [idleCountdown, setIdleCountdown] = useState(inactivityMinute);
  const idleCountdownKey = 'idleCountdown';
  const auth = useAuth();
  const [open, setOpen] = useState(false);
  const isIdleTimeoutEnabled = useSelector(selectIsIdleTimeoutEnabled);

  useEffect(() => {
    setIdleCountdown(inactivityMinute);
  }, [inactivityMinute]);

  const handleOnIdle = () => {
    if (auth.isUserLoggedIn) {
      setOpen(true);
      idleTimer.pause();
    }
  };

  const handleOnAction = () => {
    if (auth.isUserLoggedIn) {
      const now = new Date().getTime();
      const obj = { value: idleCountdown.toString(), time: now };
      localStorage.setItem(idleCountdownKey, JSON.stringify(obj));
    }
  };

  const debounceTime = 300;
  const idleTimer = useIdleTimer({
    timeout: 1000 * 60 * idleCountdown + debounceTime,
    onIdle: handleOnIdle,
    onAction: handleOnAction,
    startOnMount: false,
    stopOnIdle: true,
    debounce: debounceTime,
  });

  useEffect(() => {
    if (auth.isUserLoggedIn && idleCountdown !== 0 && isIdleTimeoutEnabled) {
      idleTimer.reset();
      idleTimer.resume();
    } else {
      idleTimer.pause();
    }
  }, [auth.isUserLoggedIn, idleCountdown, idleTimer, isIdleTimeoutEnabled]);

  useEffect(() => {
    const setIdle = (e: any) => {
      if (e.key === idleCountdownKey) {
        const idle = localStorage.getItem(idleCountdownKey);
        if (idle) {
          setIdleCountdown(parseInt(JSON.parse(idle).value, 10));
          idleTimer.reset();
        }
      }
    };
    window.addEventListener('storage', setIdle);
    return () => window.removeEventListener('storage', setIdle);
  }, [idleTimer]);

  return {
    open,
    setOpen,
  };
};
